// composables/useRoster.js
import { ref, computed } from 'vue';
import axiosClient from '@/axiosClient';
import { API_ENDPOINTS } from '@/apiConfig';
import { useEmployeePhotos } from '@/composables/useEmployeePhotos';

export function useRoster(viewType = 'roster') {
  const allRosters = ref([]);
  const rosterLoading = ref(false);
  const rosterErrors = ref(null);
  const rosterPagination = ref({
    page: 1,
    page_size: 100,
    total_items: 0,
    total_pages: 1
  });
  const totalDurationMinutes = ref(0);
  const { attachEmployeePhotos } = useEmployeePhotos();

  const safeAllRosters = computed(() => {
    return Array.isArray(allRosters.value) ? allRosters.value : [];
  });

  const setRosters = (rosters) => {
    const rostersWithPhotos = attachEmployeePhotos(rosters);
    allRosters.value = Array.isArray(rostersWithPhotos) ? rostersWithPhotos : [];
  };

  const resetSearchMetadata = () => {
    rosterPagination.value = {
      page: 1,
      page_size: 100,
      total_items: 0,
      total_pages: 1
    };
    totalDurationMinutes.value = 0;
  };

  const fetchAllRosters = async (employeeId, companyId, startDate, endDate) => {
    rosterLoading.value = true;
    rosterErrors.value = null;

    try {
      const url = viewType === 'leaves' 
        ? API_ENDPOINTS.fetchLeaves
            .replace('{employeeId}', employeeId)
            .replace('{companyId}', companyId)
            .replace('{startDate}', startDate || 'None')
            .replace('{endDate}', endDate || 'None')
        : API_ENDPOINTS.fetchRosters
            .replace('{employeeId}', employeeId)
            .replace('{companyId}', companyId)
            .replace('{startDate}', startDate || 'None')
            .replace('{endDate}', endDate || 'None');

      const response = await axiosClient.get(url);
      
      if (response.data && Array.isArray(response.data)) {
        setRosters(response.data);
        rosterPagination.value = {
          page: 1,
          page_size: response.data.length || 100,
          total_items: response.data.length,
          total_pages: 1
        };
      } else {
        console.warn('Received non-array data from API:', response.data);
        setRosters([]);
        resetSearchMetadata();
      }
    } catch (err) {
      rosterErrors.value = err.response?.data?.message || err.message || 
        `Failed to fetch ${viewType} data`;
      setRosters([]);
      resetSearchMetadata();
    } finally {
      rosterLoading.value = false;
    }
  };

  const fetchRostersByEmployees = async (
    employeeIds,
    startDate,
    endDate,
    {
      companyId = null,
      timezone = 'Europe/London',
      shiftType = 'ALL',
      markedStatus = 'ALL',
      page = 1,
      pageSize = 100,
      sortOrder = 'ASC'
    } = {}
  ) => {
    rosterLoading.value = true;
    rosterErrors.value = null;

    try {
      const url = viewType === 'leaves'
        ? API_ENDPOINTS.searchLeaves
        : API_ENDPOINTS.searchRosters;
      const response = await axiosClient.post(url, {
        company_id: companyId,
        employee_ids: employeeIds,
        start_date: startDate,
        end_date: endDate,
        timezone,
        shift_type: shiftType,
        marked_status: markedStatus,
        page,
        page_size: pageSize,
        sort_order: sortOrder
      });

      const result = response.data;
      if (
        !result
        || !Array.isArray(result.items)
        || !result.pagination
        || !Number.isInteger(result.pagination.page)
        || !Number.isInteger(result.pagination.page_size)
        || !Number.isInteger(result.pagination.total_items)
        || !Number.isInteger(result.pagination.total_pages)
        || !Number.isInteger(result.total_duration_minutes)
      ) {
        throw new Error(`Invalid ${viewType} response: expected paginated data.`);
      }

      setRosters(result.items);
      rosterPagination.value = result.pagination;
      totalDurationMinutes.value = result.total_duration_minutes;
      return true;
    } catch (err) {
      rosterErrors.value = err.response?.data?.error || err.message ||
        `Failed to fetch ${viewType} data`;
      setRosters([]);
      resetSearchMetadata();
      return false;
    } finally {
      rosterLoading.value = false;
    }
  };

  return {
    allRosters: safeAllRosters,
    rosterLoading,
    rosterErrors,
    rosterPagination,
    totalDurationMinutes,
    fetchAllRosters,
    fetchRostersByEmployees
  };
}
